import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { jobs as jobsApi, tokens } from '../utils/api';
import Alert from '../components/Alert';
import LocationSelector from '../components/LocationSelector';
import toast from 'react-hot-toast';


const TYPES = ['JOB', 'SERVICE', 'TASK', 'FREELANCE'];
const CATEGORIES = ['Technical', 'Domestic', 'Freelance', 'Tutor', 'Creative', 'Local Task'];
const POST_COST = 5;

export default function PostJob() {
  const { t, user, fetchBalance } = useApp();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', type: 'JOB',
    category: 'Technical', location: '', budget: '', deadline: '',
    reward_tokens: 0,
  });


  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    tokens.balance().then(r => setBalance(r.balance)).catch(() => { });
  }, [user, navigate]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    const totalCost = POST_COST + (parseInt(form.reward_tokens) || 0);
    if (balance < totalCost) {
      toast.error(`Need ${totalCost} tokens. You have ${balance}.`); return;
    }

    setLoading(true);
    try {
      await jobsApi.create(form);
      fetchBalance();
      toast.success(t('job_posted'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.error || t('error_occurred'));
    } finally { setLoading(false); }
  };

  return (
    <div className="container-sm" style={{ paddingTop: '36px', paddingBottom: '60px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>{t('post_job')}</h1>
      <p style={{ color: 'var(--text3)', fontSize: '14px', marginBottom: '20px' }}>
        Post your job and receive applications from qualified seekers.
      </p>

      <Alert
        type={balance !== null && balance < (POST_COST + (parseInt(form.reward_tokens) || 0)) ? 'error' : 'info'}
        message={
          balance !== null && balance < (POST_COST + (parseInt(form.reward_tokens) || 0))
            ? `Insufficient tokens. This post costs ${POST_COST + (parseInt(form.reward_tokens) || 0)} tokens (including ${form.reward_tokens} reward), you have ${balance}.`
            : `Posting costs ${POST_COST} tokens + your chosen reward. Your balance: ${balance ?? '…'} tokens.`
        }
      />


      <form className="card" onSubmit={handleSubmit} style={{ marginTop: '16px' }}>
        <div className="form-group">
          <label className="form-label">{t('job_title')} *</label>
          <input className="form-input" required value={form.title} onChange={set('title')}
            placeholder="e.g. React developer needed for 2 weeks" />
        </div>

        <div className="form-group">
          <label className="form-label">{t('description')} *</label>
          <textarea className="form-input" required rows={5} value={form.description}
            onChange={set('description')}
            placeholder="Describe the job in detail — requirements, skills needed, what the worker should bring..."
            style={{ resize: 'vertical' }} />
        </div>

        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">{t('type')} *</label>
            <select className="form-input" value={form.type} onChange={set('type')}>
              {TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('category')} *</label>
            <select className="form-input" value={form.category} onChange={set('category')}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t('location')} *</label>
          <LocationSelector
            initialValue={form.location}
            onChange={val => setForm(f => ({ ...f, location: val }))}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('budget')} *</label>
          <input className="form-input" type="number" required min="1" value={form.budget}
            onChange={set('budget')} placeholder="e.g. 5000" />
        </div>


        <div className="form-group">
          <label className="form-label">Reward Gift Tokens *</label>
          <input className="form-input" type="number" required min="0" value={form.reward_tokens}
            onChange={set('reward_tokens')} placeholder="e.g. 5" />
          <p style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '4px' }}>
            Tokens gifted to seeker from your balance upon job completion verification.
          </p>
        </div>


        <div className="form-group">
          <label className="form-label">Deadline (optional)</label>
          <input className="form-input" type="date" value={form.deadline} onChange={set('deadline')}
            min={new Date().toISOString().split('T')[0]} />
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <button type="button" className="btn btn-outline" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-full"
            disabled={loading || (balance !== null && balance < (POST_COST + (parseInt(form.reward_tokens) || 0)))}>
            {loading
              ? <span className="spinner" style={{ width: 16, height: 16 }} />
              : `${t('post_job')} (−${POST_COST + (parseInt(form.reward_tokens) || 0)} ${t('tokens')})`}
          </button>

        </div>
      </form>
    </div>
  );
}
